import { useState, useEffect } from 'react';
import { Droplets, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { WaterRestrictionCityDrawer } from './WaterRestrictionCityDrawer';
import {
  DAYS,
  DAY_LABELS,
  type DayAbbr,
  type AssignmentType,
  type WaterClass,
  type CityScheduleEntry,
  type WaterRule,
  type WaterSchedule,
  resolveRuleDays,
  formatDays,
} from '../lib/waterRestrictions';

// --- per-class rule edit state ----------------------------------------------
interface RuleState {
  enabled: boolean;
  assignment_type: AssignmentType;
  odd_days: DayAbbr[];
  even_days: DayAbbr[];
  fixed_days: DayAbbr[];
  by_last_digit: Record<string, DayAbbr[]>;
  noWater: boolean;
  noWaterStart: string;
  noWaterEnd: string;
  seasonal: boolean;
  seasonStart: string;
  seasonEnd: string;
  maxDays: string;
  dripExempt: boolean;
}

const toDayAbbrs = (arr?: string[]): DayAbbr[] =>
  DAYS.filter((d) => (arr ?? []).some((x) => x.toLowerCase().startsWith(d.toLowerCase().slice(0, 3))));

function defaultRule(enabled: boolean): RuleState {
  return {
    enabled,
    assignment_type: 'parity',
    odd_days: [],
    even_days: [],
    fixed_days: [],
    by_last_digit: Object.fromEntries(Array.from({ length: 10 }, (_, i) => [String(i), [] as DayAbbr[]])),
    noWater: true,
    noWaterStart: '10:00',
    noWaterEnd: '18:00',
    seasonal: false,
    seasonStart: '04-01',
    seasonEnd: '10-31',
    maxDays: '2',
    dripExempt: true,
  };
}

function ruleToState(rule: WaterRule | null | undefined): RuleState {
  if (!rule) return defaultRule(false);
  return {
    enabled: true,
    assignment_type: rule.assignment_type ?? 'parity',
    odd_days: toDayAbbrs(rule.odd_days),
    even_days: toDayAbbrs(rule.even_days),
    fixed_days: toDayAbbrs(rule.fixed_days),
    by_last_digit: Object.fromEntries(
      Array.from({ length: 10 }, (_, i) => [String(i), toDayAbbrs(rule.by_last_digit?.[String(i)])]),
    ),
    noWater: !!rule.no_water_window,
    noWaterStart: rule.no_water_window?.start ?? '10:00',
    noWaterEnd: rule.no_water_window?.end ?? '18:00',
    seasonal: !!rule.seasonal,
    seasonStart: rule.seasonal?.start ?? '04-01',
    seasonEnd: rule.seasonal?.end ?? '10-31',
    maxDays: rule.max_days_per_week != null ? String(rule.max_days_per_week) : '2',
    dripExempt: rule.drip_exempt ?? true,
  };
}

function stateToRule(rs: RuleState): WaterRule | null {
  if (!rs.enabled) return null;
  const r: WaterRule = { assignment_type: rs.assignment_type };
  if (rs.assignment_type === 'parity') {
    r.odd_days = rs.odd_days;
    r.even_days = rs.even_days;
  } else if (rs.assignment_type === 'fixed') {
    r.fixed_days = rs.fixed_days;
  } else if (rs.assignment_type === 'last_digit') {
    r.by_last_digit = rs.by_last_digit;
  }
  r.no_water_window = rs.noWater ? { start: rs.noWaterStart, end: rs.noWaterEnd } : null;
  r.seasonal = rs.seasonal ? { start: rs.seasonStart, end: rs.seasonEnd } : null;
  r.max_days_per_week = rs.maxDays ? Number(rs.maxDays) : null;
  r.drip_exempt = rs.dripExempt;
  return r;
}

// --- whole-modal edit state -------------------------------------------------
interface EditState {
  scheduleId: string | null;
  city: string;
  state: string;
  authority: string;
  authority_type: 'city' | 'county' | 'district' | 'unknown';
  source_url: string;
  last_verified: string;
  notes: string;
  is_active: boolean;
  tab: WaterClass;
  rules: Record<WaterClass, RuleState>;
}

function entryToEdit(e: CityScheduleEntry): EditState {
  return {
    scheduleId: e.schedule?.id ?? null,
    city: e.city,
    state: e.schedule?.state ?? 'TX',
    authority: e.schedule?.authority ?? '',
    authority_type: e.schedule?.authority_type ?? 'city',
    source_url: e.schedule?.source_url ?? '',
    last_verified: e.schedule?.last_verified?.slice(0, 10) ?? '',
    notes: e.schedule?.notes ?? '',
    is_active: e.schedule?.is_active ?? true,
    tab: 'non_residential',
    rules: {
      // a brand-new city defaults to a non-residential rule enabled (most properties)
      non_residential: ruleToState(e.schedule?.schedule.non_residential ?? (e.schedule ? null : undefined)),
      residential: ruleToState(e.schedule?.schedule.residential),
    },
  };
}

function editToSchedule(e: EditState): WaterSchedule {
  return {
    residential: stateToRule(e.rules.residential),
    non_residential: stateToRule(e.rules.non_residential),
  };
}

/** Short human summary of one class's rule for the list. */
function summarizeRule(rule: WaterRule | null | undefined): string {
  if (!rule) return '';
  if (rule.assignment_type === 'parity')
    return `Odd ${formatDays(toDayAbbrs(rule.odd_days))} · Even ${formatDays(toDayAbbrs(rule.even_days))}`;
  if (rule.assignment_type === 'fixed') return formatDays(toDayAbbrs(rule.fixed_days));
  if (rule.assignment_type === 'last_digit') return 'By last digit of address';
  return 'Time-of-day only';
}

function DayPicker({ value, onChange }: { value: DayAbbr[]; onChange: (d: DayAbbr[]) => void }) {
  const toggle = (d: DayAbbr) => onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d]);
  return (
    <div className="flex flex-wrap gap-1">
      {DAYS.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => toggle(d)}
          className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${
            value.includes(d)
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-surface text-theme-secondary border-line hover:bg-subtle'
          }`}
          title={DAY_LABELS[d]}
        >
          {d}
        </button>
      ))}
    </div>
  );
}

export function WaterRestrictionSchedulesManagement() {
  const { notify, NotifyModal } = useNotify();
  const [items, setItems] = useState<CityScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [drawerEntry, setDrawerEntry] = useState<CityScheduleEntry | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await apiClient.get<CityScheduleEntry[]>('/api/water-restriction-schedules'));
    } catch (err) {
      console.error('Failed to load water restriction schedules:', err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!edit) return;
    setSaving(true);
    const payload = {
      city: edit.city,
      state: edit.state.trim() || 'TX',
      authority: edit.authority.trim() || null,
      authority_type: edit.authority_type,
      schedule: editToSchedule(edit),
      source_url: edit.source_url.trim() || null,
      last_verified: edit.last_verified || null,
      notes: edit.notes.trim() || null,
      is_active: edit.is_active,
    };
    try {
      if (edit.scheduleId) await apiClient.put(`/api/water-restriction-schedules/${edit.scheduleId}`, payload);
      else await apiClient.post('/api/water-restriction-schedules', payload);
      setEdit(null);
      await load();
    } catch (err) {
      console.error('Failed to save schedule:', err);
      notify('Error', 'Failed to save the schedule. Please try again.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.del(`/api/water-restriction-schedules/${deleteId}`);
      setDeleteId(null);
      await load();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      notify('Error', 'Failed to delete the schedule.', 'danger');
    }
  };

  const definedCount = items.filter((i) => i.schedule).length;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary flex items-center gap-2">
            <Droplets size={24} /> Water Restriction Schedules
          </h1>
          <p className="text-sm text-theme-secondary">
            The days &amp; times each city allows irrigation. Define a rule once per city — separately for
            non-residential (commercial / HOA common areas) and residential customers — and every property inherits
            its city&apos;s rule based on its class. {definedCount}/{items.length} cities defined.
          </p>
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-line overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-theme-secondary">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-theme-secondary">
            No cities found on properties yet. Run the water-restriction backfill to populate city/ZIP from Aspire.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-subtle text-left text-xs uppercase tracking-wide text-theme-secondary">
              <tr>
                <th className="px-4 py-2 font-semibold">City</th>
                <th className="px-4 py-2 font-semibold">Properties</th>
                <th className="px-4 py-2 font-semibold">Non-residential / HOA</th>
                <th className="px-4 py-2 font-semibold">Residential</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((e) => (
                <tr key={e.city} className="hover:bg-subtle">
                  <td className="px-4 py-3 font-medium text-theme-primary">
                    {e.city}
                    {e.schedule?.authority && (
                      <div className="text-xs font-normal text-theme-secondary">{e.schedule.authority}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-theme-secondary">
                    {e.property_count > 0 ? (
                      <button
                        onClick={() => setDrawerEntry(e)}
                        className="font-medium text-blue-600 underline-offset-2 hover:underline"
                        title="View compliance breakdown"
                      >
                        {e.property_count}
                      </button>
                    ) : (
                      e.property_count
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-theme-secondary">{summarizeRule(e.schedule?.schedule.non_residential)}</td>
                  <td className="px-4 py-3 text-xs text-theme-secondary">{summarizeRule(e.schedule?.schedule.residential)}</td>
                  <td className="px-4 py-3">
                    {!e.schedule ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                        <AlertTriangle size={12} /> Needs schedule
                      </span>
                    ) : e.schedule.authority_type === 'unknown' ? (
                      <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                        Unknown provider
                      </span>
                    ) : e.schedule.is_active ? (
                      <span className="inline-block rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300">
                        Active
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-subtle px-2 py-0.5 text-xs font-semibold text-theme-secondary">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEdit(entryToEdit(e))}
                      className="p-1.5 rounded text-theme-secondary hover:bg-subtle"
                      title={e.schedule ? 'Edit' : 'Define schedule'}
                    >
                      <Pencil size={16} />
                    </button>
                    {e.schedule && (
                      <button
                        onClick={() => setDeleteId(e.schedule!.id)}
                        className="p-1.5 rounded text-red-500 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edit && (
        <EditModal edit={edit} setEdit={setEdit} saving={saving} onSave={save} onClose={() => !saving && setEdit(null)} />
      )}

      <ConfirmModal
        isOpen={deleteId !== null}
        title="Delete schedule"
        message="This removes the rule for this city. Properties in it will fall back to having no defined schedule. Continue?"
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />

      {drawerEntry && <WaterRestrictionCityDrawer entry={drawerEntry} onClose={() => setDrawerEntry(null)} />}

      {NotifyModal}
    </div>
  );
}

// --- the rule editor for a single class -------------------------------------
function RuleEditor({ rule, onChange }: { rule: RuleState; onChange: (r: RuleState) => void }) {
  const set = <K extends keyof RuleState>(k: K, v: RuleState[K]) => onChange({ ...rule, [k]: v });
  const builtRule = stateToRule({ ...rule, enabled: true });
  const oddPreview = formatDays(resolveRuleDays(builtRule, 2501));
  const evenPreview = formatDays(resolveRuleDays(builtRule, 7200));

  if (!rule.enabled) {
    return (
      <div className="rounded-lg border border-dashed border-line p-4 text-sm text-theme-secondary">
        No rule defined for this class.{' '}
        <button onClick={() => set('enabled', true)} className="text-blue-600 font-medium hover:underline">
          Add a rule
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end -mt-1">
        <button onClick={() => set('enabled', false)} className="text-xs text-red-500 hover:underline">
          Remove this class&apos;s rule
        </button>
      </div>

      <div>
        <label className="block text-sm font-semibold text-theme-primary mb-1">How allowed days are assigned</label>
        <select
          value={rule.assignment_type}
          onChange={(e) => set('assignment_type', e.target.value as AssignmentType)}
          className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="parity">By address — odd vs even street number</option>
          <option value="last_digit">By last digit of street number</option>
          <option value="fixed">Same days for everyone in this class</option>
          <option value="none">No day restriction (time-of-day only)</option>
        </select>
      </div>

      {rule.assignment_type === 'parity' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-theme-secondary mb-1">Odd addresses water on</label>
            <DayPicker value={rule.odd_days} onChange={(d) => set('odd_days', d)} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-theme-secondary mb-1">Even addresses water on</label>
            <DayPicker value={rule.even_days} onChange={(d) => set('even_days', d)} />
          </div>
        </div>
      )}

      {rule.assignment_type === 'fixed' && (
        <div>
          <label className="block text-xs font-semibold text-theme-secondary mb-1">Allowed days (all addresses in this class)</label>
          <DayPicker value={rule.fixed_days} onChange={(d) => set('fixed_days', d)} />
        </div>
      )}

      {rule.assignment_type === 'last_digit' && (
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-theme-secondary">Allowed days by last digit of street number</label>
          {Array.from({ length: 10 }, (_, i) => String(i)).map((digit) => (
            <div key={digit} className="flex items-center gap-3">
              <span className="w-4 text-xs font-mono text-theme-secondary">{digit}</span>
              <DayPicker
                value={rule.by_last_digit[digit] ?? []}
                onChange={(d) => set('by_last_digit', { ...rule.by_last_digit, [digit]: d })}
              />
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-line pt-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-theme-primary">
          <input type="checkbox" checked={rule.noWater} onChange={(e) => set('noWater', e.target.checked)} className="h-4 w-4" />
          No watering during a daily time window
        </label>
        {rule.noWater && (
          <div className="flex items-center gap-2 mt-2 text-sm text-theme-secondary">
            <span>From</span>
            <input type="time" value={rule.noWaterStart} onChange={(e) => set('noWaterStart', e.target.value)} className="px-2 py-1 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
            <span>to</span>
            <input type="time" value={rule.noWaterEnd} onChange={(e) => set('noWaterEnd', e.target.value)} className="px-2 py-1 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
          </div>
        )}
      </div>

      <div className="border-t border-line pt-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-theme-primary">
          <input type="checkbox" checked={rule.seasonal} onChange={(e) => set('seasonal', e.target.checked)} className="h-4 w-4" />
          Rule only applies seasonally
        </label>
        {rule.seasonal && (
          <div className="flex items-center gap-2 mt-2 text-sm text-theme-secondary">
            <span>From</span>
            <input type="text" value={rule.seasonStart} onChange={(e) => set('seasonStart', e.target.value)} placeholder="MM-DD" className="w-24 px-2 py-1 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
            <span>to</span>
            <input type="text" value={rule.seasonEnd} onChange={(e) => set('seasonEnd', e.target.value)} placeholder="MM-DD" className="w-24 px-2 py-1 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-line pt-4">
        <div>
          <label className="block text-sm font-semibold text-theme-primary mb-1">Max days per week</label>
          <input type="number" min={0} max={7} value={rule.maxDays} onChange={(e) => set('maxDays', e.target.value)} className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm text-theme-primary mt-7">
          <input type="checkbox" checked={rule.dripExempt} onChange={(e) => set('dripExempt', e.target.checked)} className="h-4 w-4" />
          Drip irrigation exempt
        </label>
      </div>

      {rule.assignment_type === 'parity' && (
        <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-900 dark:bg-blue-500/10 dark:border-blue-500/30 dark:text-blue-200">
          <div className="font-semibold mb-0.5">Preview</div>
          <div>Odd address (e.g. 2501): {oddPreview}</div>
          <div>Even address (e.g. 7200): {evenPreview}</div>
        </div>
      )}
    </div>
  );
}

function EditModal({
  edit,
  setEdit,
  saving,
  onSave,
  onClose,
}: {
  edit: EditState;
  setEdit: (e: EditState) => void;
  saving: boolean;
  onSave: () => void;
  onClose: () => void;
}) {
  const set = <K extends keyof EditState>(k: K, v: EditState[K]) => setEdit({ ...edit, [k]: v });
  const tabs: WaterClass[] = ['non_residential', 'residential'];
  const tabLabel: Record<WaterClass, string> = {
    non_residential: 'Non-residential / HOA',
    residential: 'Residential',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-surface rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(ev) => ev.stopPropagation()}>
        <div className="px-5 py-3 border-b border-line">
          <h2 className="text-base font-bold text-theme-primary">{edit.city} — Water Restriction Schedule</h2>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {/* Authority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-theme-primary mb-1">Authority / water provider</label>
              <input type="text" value={edit.authority} onChange={(e) => set('authority', e.target.value)} placeholder="e.g. Fort Worth Water" className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-theme-primary mb-1">Authority type</label>
              <select value={edit.authority_type} onChange={(e) => set('authority_type', e.target.value as EditState['authority_type'])} className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                <option value="city">City</option>
                <option value="county">County</option>
                <option value="district">Water district / MUD</option>
                <option value="unknown">Unknown — needs manual research</option>
              </select>
            </div>
          </div>

          {/* Class tabs */}
          <div>
            <div className="flex gap-1 border-b border-line">
              {tabs.map((c) => (
                <button
                  key={c}
                  onClick={() => set('tab', c)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                    edit.tab === c ? 'border-blue-600 text-blue-700' : 'border-transparent text-theme-secondary hover:text-theme-primary'
                  }`}
                >
                  {tabLabel[c]}
                  {edit.rules[c].enabled && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />}
                </button>
              ))}
            </div>
            <p className="text-xs text-theme-secondary mt-2">
              {edit.tab === 'non_residential'
                ? 'Applies to warehouses, commercial sites, and HOA common-area irrigation.'
                : 'Applies to residential properties (single-family meters).'}
            </p>
            <div className="mt-3">
              <RuleEditor rule={edit.rules[edit.tab]} onChange={(r) => set('rules', { ...edit.rules, [edit.tab]: r })} />
            </div>
          </div>

          {/* Shared metadata */}
          <div className="grid grid-cols-2 gap-3 border-t border-line pt-4">
            <div>
              <label className="block text-sm font-semibold text-theme-primary mb-1">Source URL</label>
              <input type="url" value={edit.source_url} onChange={(e) => set('source_url', e.target.value)} placeholder="https://city.gov/watering" className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-theme-primary mb-1">Last verified</label>
              <input type="date" value={edit.last_verified} onChange={(e) => set('last_verified', e.target.value)} className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-theme-primary mb-1">Notes</label>
            <textarea value={edit.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="Variances, commercial caveats, MUD boundaries, etc." className="w-full px-3 py-2 bg-surface text-theme-primary border border-line rounded-lg text-sm" />
          </div>

          <label className="flex items-center gap-2 text-sm text-theme-primary">
            <input type="checkbox" checked={edit.is_active} onChange={(e) => set('is_active', e.target.checked)} className="h-4 w-4" />
            Active
          </label>
        </div>

        <div className="px-5 py-3 border-t border-line flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg border border-line text-theme-primary hover:bg-subtle text-sm font-medium disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
